import { Button, Empty, Input, Modal } from "antd";
import { createStyles } from "antd-style";
import { memo, useMemo, useState } from "react";
import ModelIcon, { ICON_KEYS } from "../ModelIcon";

interface IconPickerProps {
  value?: string;
  onChange?: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;

  width?: number | string;
}

const useStyles = createStyles(({ css, token }) => ({
  trigger: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 11px;
    border: 1px solid ${token.colorBorder};
    border-radius: ${token.borderRadius}px;
    background: ${token.colorBgContainer};
    color: ${token.colorText};
    cursor: pointer;
    transition: all 0.2s;
    line-height: 1;
    box-sizing: border-box;
    font: inherit;
    text-align: left;

    &:hover {
      background: ${token.colorFillQuaternary};
      border-color: ${token.colorBorder};
    }
  `,
  triggerDisabled: css`
    background: ${token.colorBgContainerDisabled};
    color: ${token.colorTextDisabled};
    cursor: not-allowed;
    &:hover {
      border-color: ${token.colorBorder};
    }
  `,
  triggerPlaceholder: css`
    color: ${token.colorTextPlaceholder};
  `,
  keyMono: css`
    font-family: ${token.fontFamilyCode};
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  search: css`
    margin-bottom: 12px;
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    max-height: 480px;
    overflow-y: auto;
    padding: 4px 2px;
  `,
  card: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 14px 8px;
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadius}px;
    background: ${token.colorBgContainer};
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1;

    &:hover {
      background: ${token.colorFillQuaternary};
      border-color: ${token.colorBorder};
    }
  `,
  cardActive: css`
    border-color: ${token.colorBorder};
    background: ${token.controlItemBgActive};
  `,
  cardKey: css`
    font-family: ${token.fontFamilyCode};
    font-size: 12px;
    color: ${token.colorTextSecondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  `,
  customCard: css`
    grid-column: 1 / -1;
    border-style: dashed;
  `,
  empty: css`
    grid-column: 1 / -1;
    padding: 40px 0;
  `,
}));

function IconPickerInner({
  value = "",
  onChange,
  placeholder = "点击选择品牌图标",
  disabled,
  width = 280,
}: IconPickerProps) {
  const { styles, cx } = useStyles();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return ICON_KEYS;
    return ICON_KEYS.filter((k) => k.toLowerCase().includes(kw));
  }, [keyword]);

  const customKey = keyword.trim().toLowerCase();
  const showCustomCard = customKey && !ICON_KEYS.includes(customKey);

  const handleOpen = () => {
    if (disabled) return;
    setKeyword("");
    setOpen(true);
  };

  const handlePick = (next: string) => {
    onChange?.(next);
    setOpen(false);
  };

  const handleClear = () => {
    onChange?.("");
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        className={cx(styles.trigger, disabled && styles.triggerDisabled)}
        style={{ width, minWidth: width }}
        onClick={handleOpen}
      >
        {value ? (
          <>
            <ModelIcon iconKey={value} size={18} />
            <span className={styles.keyMono}>{value}</span>
          </>
        ) : (
          <span className={cx(styles.keyMono, styles.triggerPlaceholder)}>
            {placeholder}
          </span>
        )}
      </button>
      <Modal
        title="选择品牌图标"
        open={open}
        onCancel={() => setOpen(false)}
        width={680}
        destroyOnHidden
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button type="link" disabled={!value} onClick={handleClear}>
              清空选择
            </Button>
            <Button onClick={() => setOpen(false)}>取消</Button>
          </div>
        }
      >
        <Input
          className={styles.search}
          placeholder="搜索"
          allowClear
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <div className={styles.grid}>
          {showCustomCard && (
            <div
              className={cx(styles.card, styles.customCard)}
              onClick={() => handlePick(customKey)}
            >
              <ModelIcon
                iconKey={customKey}
                fallbackLabel={customKey}
                size={28}
              />
              <span className={styles.cardKey}>使用自定义: {customKey}</span>
            </div>
          )}
          {filtered.length === 0 && !showCustomCard ? (
            <div className={styles.empty}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="未找到匹配的品牌"
              />
            </div>
          ) : (
            filtered.map((k) => (
              <div
                key={k}
                className={cx(styles.card, value === k && styles.cardActive)}
                onClick={() => handlePick(k)}
              >
                <ModelIcon iconKey={k} size={28} />
                <span className={styles.cardKey}>{k}</span>
              </div>
            ))
          )}
        </div>
      </Modal>
    </>
  );
}

const IconPicker = memo(IconPickerInner);
export default IconPicker;
